import type { ApplicationId, FileRole, LibraryFile, PutFileOptions } from './types';

export const LIBRARY_DB = 'editall.vault';
const CHUNK_SIZE = 4 * 1024 * 1024;
let database: Promise<IDBDatabase> | undefined;
const writes = new Map<string, Promise<unknown>>();

function request<T>(operation: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    operation.onsuccess = () => resolve(operation.result);
    operation.onerror = () => reject(operation.error ?? new Error('Ошибка IndexedDB'));
  });
}
function complete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = transaction.onerror = () => reject(transaction.error ?? new Error('Не удалось сохранить файл в IndexedDB'));
  });
}
export function openLibrary(): Promise<IDBDatabase> {
  database ??= new Promise((resolve, reject) => {
    const operation = indexedDB.open(LIBRARY_DB, 1);
    operation.onupgradeneeded = () => {
      const db = operation.result;
      const files = db.createObjectStore('files', { keyPath: 'id' });
      files.createIndex('appId', 'appId');
      files.createIndex('appPath', ['appId', 'path'], { unique: true });
      db.createObjectStore('chunks', { keyPath: 'key' });
      db.createObjectStore('settings', { keyPath: 'key' });
      db.createObjectStore('directories', { keyPath: 'key' });
    };
    operation.onsuccess = () => resolve(operation.result);
    operation.onerror = () => { database = undefined; reject(operation.error ?? new Error('Хранилище браузера недоступно')); };
    operation.onblocked = () => reject(new Error('Закройте другую вкладку Editall для обновления хранилища'));
  });
  return database;
}
export async function listFiles(appId?: ApplicationId, includeInternal = false): Promise<LibraryFile[]> {
  const db = await openLibrary();
  const store = db.transaction('files', 'readonly').objectStore('files');
  const files = await request<LibraryFile[]>(appId ? store.index('appId').getAll(appId) : store.getAll());
  return files.filter((file) => !file.deletedAt && (includeInternal || file.role !== 'internal')).sort((a, b) => b.updatedAt - a.updatedAt);
}
export async function findFile(appId: ApplicationId, path: string, includeDeleted = false): Promise<LibraryFile | undefined> {
  const db = await openLibrary();
  const file = await request<LibraryFile | undefined>(db.transaction('files', 'readonly').objectStore('files').index('appPath').get([appId, path]));
  return file?.deletedAt && !includeDeleted ? undefined : file;
}
export async function getFileRecord(id: string): Promise<LibraryFile | undefined> {
  const db = await openLibrary();
  const file = await request<LibraryFile | undefined>(db.transaction('files', 'readonly').objectStore('files').get(id));
  return file?.deletedAt ? undefined : file;
}
export function roleForPath(path: string): FileRole {
  let decoded = path;
  try { decoded = decodeURIComponent(path); } catch { /* A literal percent is a valid file name. */ }
  if (/\/(?:recovery|config|cache|thumbs|thumbnails)\//i.test(decoded)) return 'internal';
  if (/\.(pcraft|vectorcraft|fcproj|ecproj|designcraft|idml|pdf|apartment\.json)$/i.test(decoded) && !/cache|thumbnail|probe/i.test(decoded)) return 'project';
  if (/originals\//i.test(decoded)) return 'source';
  if (/exports?\//i.test(decoded)) return 'export';
  return 'internal';
}
export async function putFile(blob: Blob, name: string, options: PutFileOptions): Promise<LibraryFile> {
  const path = options.path ?? `/files/${options.role ?? 'source'}/${crypto.randomUUID()}/${name}`;
  const key = `${options.appId}:${path}`;
  const previousWrite = writes.get(key) ?? Promise.resolve();
  const write = previousWrite.catch(() => {}).then(async (): Promise<LibraryFile> => {
    if (typeof navigator !== 'undefined' && navigator.locks) return await navigator.locks.request(`editall-file:${key}`, () => writeFile(blob, name, { ...options, path }));
    return writeFile(blob, name, { ...options, path });
  });
  writes.set(key, write);
  try { return await write; } finally { if (writes.get(key) === write) writes.delete(key); }
}
async function writeFile(blob: Blob, name: string, options: PutFileOptions & { path: string }): Promise<LibraryFile> {
  const db = await openLibrary();
  const path = options.path;
  const previous = await findFile(options.appId, path, true);
  const id = options.id ?? previous?.id ?? crypto.randomUUID();
  const version = crypto.randomUUID();
  const chunks = Math.ceil(blob.size / CHUNK_SIZE);
  for (let index = 0; index < chunks; index++) {
    const data = await blob.slice(index * CHUNK_SIZE, (index + 1) * CHUNK_SIZE).arrayBuffer();
    const tx = db.transaction('chunks', 'readwrite');
    const done = complete(tx);
    tx.objectStore('chunks').put({ key: `${id}:${version}:${index}`, data });
    await done;
  }
  const now = Date.now();
  const file: LibraryFile = { id, appId: options.appId, path, name, role: options.role ?? 'source', mimeType: blob.type || 'application/octet-stream', size: blob.size, createdAt: previous?.createdAt ?? now, updatedAt: now, version, chunks };
  const tx = db.transaction('files', 'readwrite');
  const done = complete(tx); tx.objectStore('files').put(file); await done;
  // The new record becomes visible only after all chunks were written. Old
  // versions are collected after commit; user files are never evicted by count.
  if (previous) {
    const cleanup = db.transaction('chunks', 'readwrite');
    const cleaned = complete(cleanup);
    for (let index = 0; index < previous.chunks; index++) cleanup.objectStore('chunks').delete(`${previous.id}:${previous.version}:${index}`);
    await cleaned;
  }
  announceChange();
  return file;
}
export async function readFileBlob(id: string): Promise<File> {
  const db = await openLibrary();
  // One read transaction pins the metadata and its chunks together, even while
  // another editor replaces the same file and collects the previous version.
  const tx = db.transaction(['files', 'chunks'], 'readonly');
  const file = await request<LibraryFile | undefined>(tx.objectStore('files').get(id));
  if (!file) throw new Error('Файл не найден в библиотеке');
  const chunks = await Promise.all(Array.from({ length: file.chunks }, (_, index) => request<{ data: ArrayBuffer } | undefined>(tx.objectStore('chunks').get(`${file.id}:${file.version}:${index}`))));
  const parts = chunks.map((chunk) => {
    if (!chunk) throw new Error(`Не удалось прочитать часть файла «${file.name}»`);
    return chunk.data;
  });
  return new File(parts, file.name, { type: file.mimeType, lastModified: file.updatedAt });
}
export async function trashFile(id: string): Promise<void> {
  const file = await getFileRecord(id); if (!file) return;
  const db = await openLibrary(); const tx = db.transaction('files', 'readwrite'); const done = complete(tx);
  tx.objectStore('files').put({ ...file, deletedAt: Date.now() }); await done; announceChange();
}
export async function restoreFile(id: string): Promise<void> {
  const db = await openLibrary(); const original = await request<LibraryFile | undefined>(db.transaction('files', 'readonly').objectStore('files').get(id));
  if (!original) throw new Error('Файл не найден');
  const { deletedAt: _deleted, ...file } = original;
  const tx = db.transaction('files', 'readwrite'); const done = complete(tx); tx.objectStore('files').put(file); await done; announceChange();
}
export async function listTrashed(): Promise<LibraryFile[]> {
  const db = await openLibrary();
  const all = await request<LibraryFile[]>(db.transaction('files', 'readonly').objectStore('files').getAll());
  return all.filter((file) => file.deletedAt && file.role !== 'internal').sort((a, b) => (b.deletedAt ?? 0) - (a.deletedAt ?? 0));
}
export async function getSettings(appId: ApplicationId): Promise<Record<string, string>> {
  const db = await openLibrary(); const entries = await request<Array<{ key: string; appId: string; value: string }>>(db.transaction('settings', 'readonly').objectStore('settings').getAll());
  return Object.fromEntries(entries.filter((entry) => entry.appId === appId).map((entry) => [entry.key.slice(appId.length + 1), entry.value]));
}
export async function setSetting(appId: ApplicationId, name: string, value: string | null): Promise<void> {
  const db = await openLibrary(); const tx = db.transaction('settings', 'readwrite'); const done = complete(tx);
  if (value === null) tx.objectStore('settings').delete(`${appId}:${name}`); else tx.objectStore('settings').put({ key: `${appId}:${name}`, appId, value });
  await done;
}
export async function ensureDirectory(appId: ApplicationId, path: string): Promise<void> {
  const db = await openLibrary(); const tx = db.transaction('directories', 'readwrite'); const done = complete(tx); tx.objectStore('directories').put({ key: `${appId}:${path}`, appId, path }); await done;
}
export async function listDirectories(appId: ApplicationId): Promise<string[]> {
  const db = await openLibrary(); const entries = await request<Array<{ appId: string; path: string }>>(db.transaction('directories', 'readonly').objectStore('directories').getAll());
  return entries.filter((entry) => entry.appId === appId).map((entry) => entry.path);
}
export async function hasDirectory(appId: ApplicationId, path: string): Promise<boolean> { return (await listDirectories(appId)).includes(path); }
export async function removeDirectory(appId: ApplicationId, path: string): Promise<void> {
  const db = await openLibrary(); const tx = db.transaction('directories', 'readwrite'); const done = complete(tx); tx.objectStore('directories').delete(`${appId}:${path}`); await done;
}
function announceChange() {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('editall-library-change'));
  if (typeof BroadcastChannel !== 'undefined') { const channel = new BroadcastChannel('editall-library'); channel.postMessage('change'); channel.close(); }
}
